import { paths } from '@/routes/path'
import { formatCurrencyAmount } from '@/utils/currency'

export interface ProgrammePaymentAccountJSON {
  currency?: string | null
  bank_code?: string | null
  bank_name?: string | null
  is_active?: boolean | null
  is_default?: boolean | null
  account_name?: string | null
  instructions?: string | null
  account_number?: string | null
  amount?: number | string | null
}

export interface ProgrammeSegmentJSON {
  name?: string | null
  unit?: string | null
  order?: number | null
  length?: number | string | null
  description?: string | null
}

export interface ProgrammeAdmissionRequirementsJSON {
  requires_jamb?: boolean | null
  requires_o_level?: boolean | null
  required_documents?: string[] | null
}

export interface ProgrammeCohortJSON {
  id?: string | null
  programme_id?: string | null
  title?: string | null
  start_date?: string | null
  end_date?: string | null
  exam_date?: string | null
  first_yr_first_semester_start_date?: string | null
  first_yr_first_semester_end_date?: string | null
  first_yr_second_semester_start_date?: string | null
  first_yr_second_semester_end_date?: string | null
  second_yr_first_semester_start_date?: string | null
  second_yr_first_semester_end_date?: string | null
  second_yr_second_semester_start_date?: string | null
  second_yr_second_semester_end_date?: string | null
  first_yr_first_semester_exam_date?: string | null
  first_yr_second_semester_exam_date?: string | null
  second_yr_first_semester_exam_date?: string | null
  second_yr_second_semester_exam_date?: string | null
  capacity?: number | string | null
  fee?: number | string | null
  currency?: string | null
  payment_accounts?: ProgrammePaymentAccountJSON[] | null
  is_active?: boolean | null
  created_at?: string | null
  updated_at?: string | null
}

export interface ProgrammeJSON {
  id?: string
  title: string
  description?: string | null
  duration?: number | null
  is_active?: boolean | null
  department_id?: string | null
  type?: string | null
  accreditation?: string | null
  credits?: number | string | null
  duration_unit?: string | null
  installmentAmounts?: (number | string)[] | null
  segments?: ProgrammeSegmentJSON[] | null
  admission_requirements?: ProgrammeAdmissionRequirementsJSON | null
  cohorts?: ProgrammeCohortJSON[] | null
  created_at?: string | null
  updated_at?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}


export class ProgrammePaymentAccountModel {
  readonly currency?: string
  readonly bankCode?: string
  readonly bankName?: string
  readonly is_active: boolean
  readonly is_default: boolean
  readonly accountName?: string
  readonly instructions?: string
  readonly accountNumber?: string
  readonly amount?: number

  constructor(options: {
    currency?: string
    bankCode?: string
    bankName?: string
    is_active?: boolean
    is_default?: boolean
    accountName?: string
    instructions?: string
    accountNumber?: string
    amount?: number
  }) {
    this.currency = options.currency
    this.bankCode = options.bankCode
    this.bankName = options.bankName
    this.is_active = options.is_active ?? true
    this.is_default = options.is_default ?? false
    this.accountName = options.accountName
    this.instructions = options.instructions
    this.accountNumber = options.accountNumber
    this.amount = options.amount
  }

  static fromJSON(json: ProgrammePaymentAccountJSON): ProgrammePaymentAccountModel {
    const normalizedAmount =
      typeof json.amount === 'string'
        ? Number(json.amount)
        : json.amount ?? undefined

    return new ProgrammePaymentAccountModel({
      currency: json.currency ?? undefined,
      bankCode: json.bank_code ?? undefined,
      bankName: json.bank_name ?? undefined,
      is_active: json.is_active ?? true,
      is_default: json.is_default ?? false,
      accountName: json.account_name ?? undefined,
      instructions: json.instructions ?? undefined,
      accountNumber: json.account_number ?? undefined,
      amount: Number.isFinite(normalizedAmount) ? Number(normalizedAmount) : undefined,
    })
  }

  toJSON(): ProgrammePaymentAccountJSON {
    return {
      currency: this.currency,
      bank_code: this.bankCode,
      bank_name: this.bankName,
      is_active: this.is_active,
      is_default: this.is_default,
      account_name: this.accountName,
      instructions: this.instructions,
      account_number: this.accountNumber,
      amount: this.amount,
    }
  }

  /**
   * Returns a formatted amount label if amount is available.
   */
  get amountLabel(): string | undefined {
    return formatCurrencyAmount(this.amount, this.currency)
  }
}


export class ProgrammeModel {
  readonly id?: string
  readonly title: string
  readonly description?: string
  readonly duration?: number
  readonly is_active: boolean
  readonly department_id?: string
  readonly type?: string
  readonly accreditation?: string
  readonly credits?: number
  readonly duration_unit?: string
  readonly installmentAmounts?: number[]
  readonly segments: ProgrammeSegmentModel[]
  readonly admission_requirements?: ProgrammeAdmissionRequirementsModel
  readonly cohorts: ProgrammeCohortModel[]
  readonly createdAt?: string
  readonly updatedAt?: string
  readonly created_at?: string
  readonly updated_at?: string

  constructor(options: {
    id?: string
    title: string
    description?: string
    duration?: number
    is_active?: boolean
    departmentId?: string
    type?: string
    accreditation?: string
    credits?: number
    duration_unit?: string
    installmentAmounts?: number[]
    segments?: ProgrammeSegmentModel[]
    admission_requirements?: ProgrammeAdmissionRequirementsModel
    cohorts?: ProgrammeCohortModel[]
    createdAt?: string
    updatedAt?: string
    created_at?: string
    updated_at?: string
  }) {
    this.id = options.id ? options.id : '';
    this.title = options.title;
    this.description = options.description;
    this.duration = options.duration
    this.is_active = options.is_active ?? true
    this.is_active = this.is_active
    this.department_id = options.departmentId;
    this.type = options.type;
    this.accreditation = options.accreditation;
    this.credits = options.credits
    this.duration_unit = options.duration_unit;
    this.installmentAmounts = options.installmentAmounts
    this.segments = options.segments ?? []
    this.admission_requirements = options.admission_requirements
    this.cohorts = options.cohorts ?? []
    this.createdAt = options.createdAt ?? undefined
    this.updatedAt = options.updatedAt ?? undefined
  }

  static fromJSON(json: ProgrammeJSON): ProgrammeModel {
    const segments = Array.isArray(json.segments)
      ? json.segments
        .filter((segment): segment is ProgrammeSegmentJSON => Boolean(segment))
        .map((segment) => ProgrammeSegmentModel.fromJSON(segment))
      : []

    const installmentAmounts = Array.isArray(json.installmentAmounts)
      ? json.installmentAmounts
        .map((value) => (typeof value === 'string' ? Number(value) : value))
        .filter((value): value is number => Number.isFinite(value))
      : undefined

    const cohorts = Array.isArray(json.cohorts)
      ? json.cohorts
        .filter((cohort): cohort is ProgrammeCohortJSON => Boolean(cohort))
        .map((cohort) => ProgrammeCohortModel.fromJSON(cohort))
      : []

    const admissionRequirements = ProgrammeAdmissionRequirementsModel.fromJSON(json.admission_requirements)

    return new ProgrammeModel({
      id: json.id,
      title: json.title,
      description: json.description ?? undefined,
      duration: json.duration ?? undefined,
      is_active: (json as ProgrammeJSON & { is_active?: boolean }).is_active ?? json.is_active ?? true,
      departmentId: json.department_id ?? undefined,
      type: json.type ?? undefined,
      accreditation: json.accreditation ?? undefined,
      credits: parseOptionalNumber(json.credits),
      duration_unit: json.duration_unit ?? undefined,
      installmentAmounts,
      segments,
      admission_requirements: admissionRequirements,
      cohorts,
      createdAt: json.createdAt ?? json.created_at ?? undefined,
      updatedAt: json.updatedAt ?? json.updated_at ?? undefined,
      created_at: json.created_at ?? undefined,
      updated_at: json.updated_at ?? undefined,
    })
  }

  toJSON(): ProgrammeJSON {
    return {
      id: this.id,
      title: this.title,
      description: this.description,
      duration: this.duration,
      is_active: this.is_active,
      department_id: this.department_id,
      type: this.type,
      accreditation: this.accreditation,
      credits: this.credits,
      duration_unit: this.duration_unit,
      installmentAmounts: this.installmentAmounts,
      segments: this.segments.map((segment) => segment.toJSON()),
      admission_requirements: this.admission_requirements?.toJSON() ?? null,
      cohorts: this.cohorts.map((cohort) => cohort.toJSON()),
      created_at: this.createdAt ?? this.created_at,
      updated_at: this.updatedAt ?? this.updated_at,
    }
  }

  /**
   * Returns the most relevant bank payment account.
   */
  getPrimaryPaymentAccount(): ProgrammePaymentAccountModel | undefined {
    const firstActiveCohort = this.cohorts.find((c) => c.isActive) || this.cohorts[0]
    if (!firstActiveCohort) return undefined

    const accounts = firstActiveCohort.payment_accounts
    const activeAccounts = accounts.filter((account) => account.is_active)
    return (
      activeAccounts.find((account) => account.is_default) ??
      activeAccounts[0] ??
      accounts.find((account) => account.is_default) ??
      accounts[0]
    )
  }

  /**
   * Friendly price label for display.
   */
  get priceLabel(): string | undefined {
    const firstActiveCohort = this.cohorts.find((c) => c.isActive) || this.cohorts[0]
    const fee = firstActiveCohort?.fee
    const currency = firstActiveCohort?.currency

    const feeLabel = formatCurrencyAmount(fee, currency)
    return feeLabel ?? this.getPrimaryPaymentAccount()?.amountLabel
  }

  /**
   * Build application URL with bank/payment metadata encoded as query params.
   */
  buildApplyUrl(basePath: string = paths.public.apply.absolute): string {
    const params = new URLSearchParams()
    params.set('programmeId', this.id ?? '')
    params.set('programmeName', this.title)

    const primaryAccount = this.getPrimaryPaymentAccount()
    const firstActiveCohort = this.cohorts.find((c) => c.isActive) || this.cohorts[0]
    const fee = firstActiveCohort?.fee ?? primaryAccount?.amount

    if (fee !== undefined && fee !== null && `${fee}`.trim() !== '') {
      params.set('fee', String(fee))
    }
    const currency = firstActiveCohort?.currency ?? primaryAccount?.currency
    if (currency) {
      params.set('currency', currency)
    }
    if (primaryAccount?.bankName) {
      params.set('bankName', primaryAccount.bankName)
    }
    if (primaryAccount?.accountNumber) {
      params.set('accountNumber', primaryAccount.accountNumber)
    }
    if (primaryAccount?.accountName) {
      params.set('accountName', primaryAccount.accountName)
    }
    if (primaryAccount?.instructions) {
      params.set('instructions', primaryAccount.instructions)
    }

    return `${basePath}?${params.toString()}`
  }
}

export interface ProgrammesResponseJSON {
  success: boolean
  message?: string
  data?: ProgrammeJSON[] | ProgrammeJSON | null
  meta?: {
    total?: number
    page?: number
    perPage?: number
  }
}

export class ProgrammeSegmentModel {
  readonly name?: string
  readonly unit?: string
  readonly order?: number
  readonly length?: number
  readonly description?: string

  constructor(options: {
    name?: string
    unit?: string
    order?: number
    length?: number
    description?: string
  }) {
    this.name = options.name;
    this.unit = options.unit;
    this.order = options.order
    this.length = options.length
    this.description = options.description;
  }

  static fromJSON(json: ProgrammeSegmentJSON): ProgrammeSegmentModel {
    return new ProgrammeSegmentModel({
      name: json.name ?? undefined,
      unit: json.unit ?? undefined,
      order: json.order ?? undefined,
      length: parseOptionalNumber(json.length),
      description: json.description ?? undefined,
    })
  }

  toJSON(): ProgrammeSegmentJSON {
    return {
      name: this.name,
      unit: this.unit,
      order: this.order,
      length: this.length,
      description: this.description,
    }
  }
}

export class ProgrammeCohortModel {
  readonly id: string
  readonly programmeId?: string
  readonly title: string
  readonly startDate?: string | null
  readonly endDate?: string | null
  readonly examDate?: string | null
  readonly firstYrFirstSemesterStartDate?: string | null
  readonly firstYrFirstSemesterEndDate?: string | null
  readonly firstYrSecondSemesterStartDate?: string | null
  readonly firstYrSecondSemesterEndDate?: string | null
  readonly secondYrFirstSemesterStartDate?: string | null
  readonly secondYrFirstSemesterEndDate?: string | null
  readonly secondYrSecondSemesterStartDate?: string | null
  readonly secondYrSecondSemesterEndDate?: string | null
  readonly firstYrFirstSemesterExamDate?: string | null
  readonly firstYrSecondSemesterExamDate?: string | null
  readonly secondYrFirstSemesterExamDate?: string | null
  readonly secondYrSecondSemesterExamDate?: string | null
  readonly capacity?: number
  readonly fee?: number
  readonly currency?: string
  readonly isActive?: boolean
  readonly payment_accounts: ProgrammePaymentAccountModel[]
  readonly createdAt?: string
  readonly updatedAt?: string

  constructor(options: {
    id?: string | null
    programmeId?: string
    title?: string | null
    startDate?: string | null
    endDate?: string | null
    examDate?: string | null
    firstYrFirstSemesterStartDate?: string | null
    firstYrFirstSemesterEndDate?: string | null
    firstYrSecondSemesterStartDate?: string | null
    firstYrSecondSemesterEndDate?: string | null
    secondYrFirstSemesterStartDate?: string | null
    secondYrFirstSemesterEndDate?: string | null
    secondYrSecondSemesterStartDate?: string | null
    secondYrSecondSemesterEndDate?: string | null
    firstYrFirstSemesterExamDate?: string | null
    firstYrSecondSemesterExamDate?: string | null
    secondYrFirstSemesterExamDate?: string | null
    secondYrSecondSemesterExamDate?: string | null
    capacity?: number
    fee?: number
    currency?: string | null
    isActive?: boolean | null
    payment_accounts?: ProgrammePaymentAccountModel[]
    createdAt?: string | null
    updatedAt?: string | null
  }) {
    this.id = options.id ?? ''
    this.programmeId = options.programmeId
    this.title = options.title ?? ''
    this.startDate = options.startDate ?? undefined
    this.endDate = options.endDate ?? undefined
    this.examDate = options.examDate ?? undefined
    this.firstYrFirstSemesterStartDate = options.firstYrFirstSemesterStartDate ?? undefined
    this.firstYrFirstSemesterEndDate = options.firstYrFirstSemesterEndDate ?? undefined
    this.firstYrSecondSemesterStartDate = options.firstYrSecondSemesterStartDate ?? undefined
    this.firstYrSecondSemesterEndDate = options.firstYrSecondSemesterEndDate ?? undefined
    this.secondYrFirstSemesterStartDate = options.secondYrFirstSemesterStartDate ?? undefined
    this.secondYrFirstSemesterEndDate = options.secondYrFirstSemesterEndDate ?? undefined
    this.secondYrSecondSemesterStartDate = options.secondYrSecondSemesterStartDate ?? undefined
    this.secondYrSecondSemesterEndDate = options.secondYrSecondSemesterEndDate ?? undefined
    this.firstYrFirstSemesterExamDate = options.firstYrFirstSemesterExamDate ?? undefined
    this.firstYrSecondSemesterExamDate = options.firstYrSecondSemesterExamDate ?? undefined
    this.secondYrFirstSemesterExamDate = options.secondYrFirstSemesterExamDate ?? undefined
    this.secondYrSecondSemesterExamDate = options.secondYrSecondSemesterExamDate ?? undefined
    this.capacity = options.capacity
    this.fee = options.fee
    this.currency = options.currency ?? undefined
    this.isActive = options.isActive ?? undefined
    this.payment_accounts = options.payment_accounts ?? []
    this.createdAt = options.createdAt ?? undefined
    this.updatedAt = options.updatedAt ?? undefined
  }

  static fromJSON(json: ProgrammeCohortJSON): ProgrammeCohortModel {
    const paymentAccounts = Array.isArray(json.payment_accounts)
      ? json.payment_accounts
        .filter((account): account is ProgrammePaymentAccountJSON => Boolean(account))
        .map((account) => ProgrammePaymentAccountModel.fromJSON(account))
      : []

    return new ProgrammeCohortModel({
      id: json.id ?? undefined,
      programmeId: json.programme_id ?? undefined,
      title: json.title ?? undefined,
      startDate: json.start_date ?? undefined,
      endDate: json.end_date ?? undefined,
      examDate: json.exam_date ?? undefined,
      firstYrFirstSemesterStartDate: json.first_yr_first_semester_start_date ?? undefined,
      firstYrFirstSemesterEndDate: json.first_yr_first_semester_end_date ?? undefined,
      firstYrSecondSemesterStartDate: json.first_yr_second_semester_start_date ?? undefined,
      firstYrSecondSemesterEndDate: json.first_yr_second_semester_end_date ?? undefined,
      secondYrFirstSemesterStartDate: json.second_yr_first_semester_start_date ?? undefined,
      secondYrFirstSemesterEndDate: json.second_yr_first_semester_end_date ?? undefined,
      secondYrSecondSemesterStartDate: json.second_yr_second_semester_start_date ?? undefined,
      secondYrSecondSemesterEndDate: json.second_yr_second_semester_end_date ?? undefined,
      firstYrFirstSemesterExamDate: json.first_yr_first_semester_exam_date ?? undefined,
      firstYrSecondSemesterExamDate: json.first_yr_second_semester_exam_date ?? undefined,
      secondYrFirstSemesterExamDate: json.second_yr_first_semester_exam_date ?? undefined,
      secondYrSecondSemesterExamDate: json.second_yr_second_semester_exam_date ?? undefined,
      capacity: parseOptionalNumber(json.capacity ?? undefined),
      fee: parseOptionalNumber(json.fee ?? undefined),
      currency: json.currency ?? undefined,
      isActive: typeof json.is_active === 'boolean' ? json.is_active : undefined,
      payment_accounts: paymentAccounts,
      createdAt: json.created_at ?? undefined,
      updatedAt: json.updated_at ?? undefined,
    })
  }

  toJSON(): ProgrammeCohortJSON {
    return {
      id: this.id,
      programme_id: this.programmeId,
      title: this.title,
      start_date: this.startDate ?? null,
      end_date: this.endDate ?? null,
      exam_date: this.examDate ?? null,
      first_yr_first_semester_start_date: this.firstYrFirstSemesterStartDate ?? null,
      first_yr_first_semester_end_date: this.firstYrFirstSemesterEndDate ?? null,
      first_yr_second_semester_start_date: this.firstYrSecondSemesterStartDate ?? null,
      first_yr_second_semester_end_date: this.firstYrSecondSemesterEndDate ?? null,
      second_yr_first_semester_start_date: this.secondYrFirstSemesterStartDate ?? null,
      second_yr_first_semester_end_date: this.secondYrFirstSemesterEndDate ?? null,
      second_yr_second_semester_start_date: this.secondYrSecondSemesterStartDate ?? null,
      second_yr_second_semester_end_date: this.secondYrSecondSemesterEndDate ?? null,
      first_yr_first_semester_exam_date: this.firstYrFirstSemesterExamDate ?? null,
      first_yr_second_semester_exam_date: this.firstYrSecondSemesterExamDate ?? null,
      second_yr_first_semester_exam_date: this.secondYrFirstSemesterExamDate ?? null,
      second_yr_second_semester_exam_date: this.secondYrSecondSemesterExamDate ?? null,
      capacity: this.capacity ?? null,
      fee: this.fee ?? null,
      currency: this.currency ?? null,
      is_active: this.isActive ?? null,
      payment_accounts: this.payment_accounts.map((account) => account.toJSON()),
      created_at: this.createdAt ?? null,
      updated_at: this.updatedAt ?? null,
    }
  }
}


export class ProgrammeAdmissionRequirementsModel {
  readonly requiresJamb: boolean
  readonly requiresOLevel: boolean
  readonly requiredDocuments: string[]

  constructor(options: {
    requiresJamb?: boolean
    requiresOLevel?: boolean
    requiredDocuments?: string[]
  }) {
    this.requiresJamb = options.requiresJamb ?? false
    this.requiresOLevel = options.requiresOLevel ?? false
    this.requiredDocuments = options.requiredDocuments ?? []
  }

  static fromJSON(
    json: ProgrammeAdmissionRequirementsJSON | null | undefined
  ): ProgrammeAdmissionRequirementsModel | undefined {
    if (!json) {
      return undefined
    }

    return new ProgrammeAdmissionRequirementsModel({
      requiresJamb: json.requires_jamb ?? undefined,
      requiresOLevel: json.requires_o_level ?? undefined,
      requiredDocuments: Array.isArray(json.required_documents)
        ? json.required_documents
          .filter((doc): doc is string => typeof doc === 'string')
          .map((doc) => doc)
        : undefined,
    })
  }

  toJSON(): ProgrammeAdmissionRequirementsJSON {
    return {
      requires_jamb: this.requiresJamb,
      requires_o_level: this.requiresOLevel,
      required_documents: this.requiredDocuments,
    }
  }
}

function parseOptionalNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const numeric = Number(value)
    if (Number.isFinite(numeric)) {
      return numeric
    }
  }

  return undefined
}
